<template>
  <span
    class="inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
    :class="TONE_CLASS[view.tone]"
    :data-testid="testId"
    :data-sla-state="view.state"
    :data-sla-outcome="view.outcome ?? undefined"
    :title="view.text"
  >
    <svg viewBox="0 0 24 24" class="h-3.5 w-3.5 shrink-0" fill="currentColor" aria-hidden="true">
      <path :d="TONE_ICON[view.tone]" />
    </svg>
    {{ view.text }}
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiCheck,
  mdiClockOutline,
  mdiProgressClock
} from '@mdi/js'

import { describeModuleViewSla } from './moduleViewSlaFormat'
import type { ModuleViewSlaTone } from './moduleViewSlaFormat'
import type { ModuleViewSla } from './types'

/**
 * Chip de SLA do contrato ModuleView (spec 2026-10-01 §7.3, G2-D12). Genérico: Registro de
 * plantão hoje, Solicitações e a visão agregada depois. O estado anda no tique (`nowMs`, um
 * relógio por tela via `useServerNowTick`); ícone + cor, o estado não depende só da cor.
 */
const props = withDefaults(
  defineProps<{ sla: ModuleViewSla | null; nowMs?: number | null; testId?: string }>(),
  { nowMs: null, testId: 'module-view-sla-chip' }
)

const view = computed(() => describeModuleViewSla(props.sla, props.nowMs))

const TONE_CLASS: Record<ModuleViewSlaTone, string> = {
  'on-time': 'bg-blue-50 text-blue-700',
  attention: 'bg-amber-100 text-amber-800',
  overdue: 'bg-red-700 text-white',
  'done-ok': 'bg-green-50 text-green-700',
  'done-neutral': 'bg-lightGray30 text-neutral50',
  none: 'border border-dashed border-neutral50/40 bg-white text-neutral50'
}

const TONE_ICON: Record<ModuleViewSlaTone, string> = {
  'on-time': mdiClockOutline,
  attention: mdiAlertOutline,
  overdue: mdiAlertCircleOutline,
  'done-ok': mdiCheck,
  'done-neutral': mdiCheck,
  none: mdiProgressClock
}
</script>
